/**
 * The captain's dialogue dictionary.
 *
 * The gate: a playtester who lost a captain says that captain's NAME an hour later,
 * unprompted. Portraits do not do that. Talk does. So this file carries the weight.
 *
 * ## Four rules every line here obeys
 *
 * 1. **A blind reader names the temperament from one line.** If two temperaments could
 *    have said it, it was cut. The seven tells live in `seed.ts` and are deliberately not
 *    restated here: a reader who wants to test this file has to be able to open it without
 *    being handed the answer key in the first paragraph.
 * 2. **Pronouns select the register, not the words.** There is no shared pool anywhere in
 *    this file. Every one of the 21 `<temperament>:<pronoun>` voices owns its own lines,
 *    written in its own grammar:
 *
 *    | register | grammar | ends on |
 *    |---|---|---|
 *    | `he` | **claims.** First person singular, terminal. Owns the object. | a noun |
 *    | `she` | **addresses.** Second person, imperative, question. Turns outward — though not always to the player: the liturgist and the firebrand talk to the dark, and that is the addressee their temperament has. | the addressee |
 *    | `they` | **deflects.** Self at one remove, then a trailing amendment. | the amendment |
 *
 *    Deflection is a concept, not a body part. Each `they` temperament deflects onto its
 *    own object: the drywit onto things and absent third parties, the liturgist onto the
 *    rite, the worrier onto the count, the veteran onto the dead, the greenhorn onto the
 *    paperwork, the clinician onto the record. Only the firebrand deflects onto the body,
 *    because the shout in the chest *is* that temperament. Six voices leaning on the same
 *    six nouns is how a shared pool hides behind a passing string-inequality test, so
 *    `tests/captain-lines.test.ts` counts body nouns per voice, bans first person singular
 *    in `they` outright, and rejects any five-word phrase two voices both use.
 *
 *    Each `she` also wants a different thing from you, and that is the differentiator
 *    rather than her vocabulary: the worrier asks questions she needs answered, the
 *    clinician instructs and never asks, the veteran passes on instructions she once got
 *    from somebody now dead, the drywit only observes, the greenhorn asks to be told, and
 *    the firebrand talks past you entirely.
 * 3. **Front-loaded.** `early` is the biggest bank in every voice by a factor of three or
 *    more. The captain talks most in the first two minutes, while nothing is at stake,
 *    because attachment has to be built before it can be spent.
 * 4. **Never mechanics.** No numbers off the HUD, no systems vocabulary. A captain talks
 *    about the cold, the dark, a rattle behind a panel, a dog, a sister, the crew they used
 *    to have, what they can hear outside the hull. `tests/captain-lines.test.ts` lints the
 *    banned vocabulary so this cannot rot.
 *
 * ## Callbacks
 *
 * `callback` lines carry `{slot}` tokens filled from `RunMemory` — a presentation-side
 * record the speech component writes from bus events (`levelup_pick`, `player_hit`,
 * `boss_spawn`, `boss_defeated`). A callback is only offered once its slots are filled, so
 * a late line can say *the thing that happened to this run* and never a placeholder.
 * `{minutes}` is spelled as a word, always: a captain says "eleven minutes", never "11".
 *
 * ## Death
 *
 * `death` is a ranked list, best first, consumed once per session per voice. The picker
 * takes the first unspent entry rather than a random one, so the strongest line is the one
 * that plays. It is the line the whole feature is judged on. Several were written per
 * voice; two survived each.
 *
 * ## Determinism
 *
 * Data and pure functions. No clock, no DOM, no storage, no `Math.random` — the picker
 * takes an `RngStream` so the same captain in the same run says the same things in the
 * same order. `tests/captain-seed.test.ts` lints this directory for all of the above.
 */

import type { RngStream } from '../core/rng';
import type { Captain, PronounId, TemperamentId, VoiceKey } from './seed';
import { PRONOUN_IDS, TEMPERAMENT_IDS, voiceKey } from './seed';

// ---------------------------------------------------------------------------
// Buckets
// ---------------------------------------------------------------------------

/**
 * The speaking situations. `callback` and `death` are separate channels because they carry
 * extra machinery (slot filling; once-per-session consumption).
 *
 * | bucket | fires when | note |
 * |---|---|---|
 * | `boarding` | once, a beat after the run opens | the first thing they ever say |
 * | `early` | ambient, first ~120 s | the attachment window; biggest bank |
 * | `wave` | something new arrives out of the dark | |
 * | `pressure` | a lot of them, close | |
 * | `hurt` | the ship took a hit | short — they are being hit |
 * | `close` | near death | |
 * | `boon` | the ship changed; something was taken on | |
 * | `boss` | a big thing arrived | |
 * | `bossdown` | it stopped moving | |
 * | `quiet` | a lull past the early window | |
 * | `deep` | past the halfway mark; the run has weight now | |
 */
export const BUCKETS = [
  'boarding',
  'early',
  'wave',
  'pressure',
  'hurt',
  'close',
  'boon',
  'boss',
  'bossdown',
  'quiet',
  'deep',
] as const;
export type Bucket = (typeof BUCKETS)[number];

/** Fires at most once per run. The component must not re-offer these. */
export const ONCE_BUCKETS: readonly Bucket[] = ['boarding'];

/**
 * What the dictionary asks of the speech component. Not timing of the reveal (that is the
 * port spec's 12/3 ms), timing of the *talking*: dense early, sparse later, and a death
 * line that does not fade on a timer.
 */
export const CADENCE = {
  /** Seconds from run start during which `early` is the ambient bucket. */
  earlyWindowS: 120,
  /**
   * Ambient gap inside the early window. Dense on purpose, and sized against the bank: at
   * 9 s the window holds about thirteen slots, reactive buckets take a few of them, and a
   * ten-line `early` bank covers the rest without leaning on one line.
   */
  earlyCooldownMs: 9000,
  /** Ambient gap after it. The captain goes quiet when the run gets serious. */
  ambientCooldownMs: 15000,
  /** Reactive buckets (`hurt`, `boss`, `bossdown`, `close`) bypass the ambient gap. */
  reactiveCooldownMs: 3500,
  /** How often a `quiet`/`deep` slot is spent on a `callback` instead, once slots exist. */
  callbackShare: 0.5,
  /**
   * The death line never auto-hides. `null` means: hold until the run-over screen takes
   * over. The player has to be allowed to sit with it.
   */
  deathHoldMs: null as number | null,
} as const;

// ---------------------------------------------------------------------------
// Run memory — the callback hook
// ---------------------------------------------------------------------------

/** Slot names a `callback` line may reference. Every one is diegetic. */
export const MEMORY_SLOTS = ['firstBoon', 'boss', 'hitter', 'minutes'] as const;
export type MemorySlot = (typeof MEMORY_SLOTS)[number];

/**
 * What the captain remembers about *this* run. Written by the speech component from the
 * bus, never by the sim. Every field is a display string already — the dictionary does no
 * lookups and knows no ids.
 */
export interface RunMemory {
  /** Display name of the first thing the ship took on. From `levelup_pick` / `gacha_pull`. */
  firstBoon?: string;
  /** Display name of the first big thing that came out of the dark. From `boss_spawn`. */
  boss?: string;
  /** Display name of whatever hurt them worst. From the largest `player_hit`. */
  hitter?: string;
  /** Whole minutes elapsed, spelled: `eleven`. Use `spellMinutes`. */
  minutes?: string;
}

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
] as const;

const TENS_WORDS = ['', '', 'twenty', 'thirty', 'forty', 'fifty'] as const;

/**
 * `11 -> 'eleven'`. A captain says the number out loud; a digit in a speech bubble reads as
 * a readout and breaks the fiction. Falls back to digits past fifty-nine, which no run
 * reaches.
 */
export function spellMinutes(n: number): string {
  const v = Math.max(0, Math.floor(n));
  if (v < NUMBER_WORDS.length) return NUMBER_WORDS[v];
  const tens = Math.floor(v / 10);
  const ones = v % 10;
  if (tens < TENS_WORDS.length) {
    return ones === 0 ? TENS_WORDS[tens] : `${TENS_WORDS[tens]}-${NUMBER_WORDS[ones]}`;
  }
  return String(v);
}

const SLOT_RE = /\{(\w+)\}/g;

/** Which slots a line needs, read off the text. Derived, so the two can never disagree. */
export function slotsIn(text: string): readonly MemorySlot[] {
  const out: MemorySlot[] = [];
  SLOT_RE.lastIndex = 0;
  let m = SLOT_RE.exec(text);
  while (m !== null) {
    const name = m[1] as MemorySlot;
    if ((MEMORY_SLOTS as readonly string[]).includes(name) && !out.includes(name)) out.push(name);
    m = SLOT_RE.exec(text);
  }
  return out;
}

/** True when every slot the line needs has been remembered. */
export function callbackReady(text: string, memory: RunMemory): boolean {
  for (const slot of slotsIn(text)) {
    const value = memory[slot];
    if (value === undefined || value.length === 0) return false;
  }
  return true;
}

/** Substitutes the slots. Unfilled slots would read as debug text, so callers gate first. */
export function fillSlots(text: string, memory: RunMemory): string {
  return text.replace(SLOT_RE, (whole, name: string) => {
    const value = memory[name as MemorySlot];
    return value === undefined || value.length === 0 ? whole : value;
  });
}

// ---------------------------------------------------------------------------
// The banks
// ---------------------------------------------------------------------------

export interface VoiceBank {
  boarding: readonly string[];
  early: readonly string[];
  wave: readonly string[];
  pressure: readonly string[];
  hurt: readonly string[];
  close: readonly string[];
  boon: readonly string[];
  boss: readonly string[];
  bossdown: readonly string[];
  quiet: readonly string[];
  deep: readonly string[];
  /** Templated against `RunMemory`. Offered only once the slots are filled. */
  callback: readonly string[];
  /** Ranked, best first. Consumed once per session. */
  death: readonly string[];
}

const VOICES: Readonly<Record<VoiceKey, VoiceBank>> = {
  // =========================================================================
  // DRY — underreacts on purpose. Domestic detail against catastrophe. Small word last.
  // =========================================================================
  'drywit:he': {
    boarding: ['Right. My ship, my rules, my thermos.'],
    early: [
      'I keep the lights low. Bulbs are expensive out here.',
      "I had a dog once. She'd have hated this. The noise, mostly.",
      "Ten years out. Still can't sleep on my left side.",
      "There's a rattle behind the fourth panel. I've decided it's structural.",
      "My mother wanted me to be a baker. She wasn't far off. Same hours.",
      'I eat standing up. Sitting down feels optimistic.',
      "That's the cold coming through the seam again. I call it Gerald.",
      "I brought one book. Read it four times. It isn't a good book.",
      "The dark out there is fine. It's the quiet I take issue with.",
      'I shave every morning. Nobody has asked me why. Good.',
    ],
    wave: ['Company. I would put the kettle on, but.', 'More of them. My afternoon was empty anyway.'],
    pressure: [
      'Getting a bit crowded for my taste.',
      'I preferred it when there were three of them.',
    ],
    hurt: ["That'll bruise. Somewhere.", 'Ow. Noted. Continuing.', 'I felt that one in a tooth.'],
    close: [
      'This is the part I usually skip.',
      "I'd like it on the record that I am not enjoying this.",
    ],
    boon: [
      "Well. That's new. I'll grow into it.",
      'Ship is heavier. Ship is angrier. Fine.',
      "I didn't ask for that. I'm keeping it.",
    ],
    boss: ['Oh, it is a big one. Marvellous.', "I was told there'd be a schedule."],
    bossdown: ['Right. Back to my book.', "It fell over. I'd call that a result."],
    quiet: [
      'Nice. Peaceful. Suspicious.',
      "I'm going to enjoy this for four seconds.",
      'Listen to that. Nothing. Lovely.',
    ],
    deep: ['Long way in. My knees know it.', "I've stopped counting. Usually a sign."],
    callback: [
      'That {boss} is still on my list. I keep a list.',
      "{minutes} minutes. I've had shorter marriages.",
      'The {hitter} still owes me a tooth.',
    ],
    death: [
      'Tell the galley not to bother with the second cup.',
      'Ten years of this and the only thing I got right was the hours. My mother said so first.',
    ],
  },

  'drywit:she': {
    boarding: ["You're new. Don't touch the panel with the tape on it."],
    early: [
      'You breathe loud when you are nervous. Everyone does. Carry on.',
      "Don't tell anyone I hum. I'll deny it and I'll be believed.",
      "You'll get used to the cold. That's a lie, but you'll get used to me saying it.",
      'If you hear knocking behind the plating, it is pipes. Probably pipes.',
      'My sister said this job would make me dull. She should see me now.',
      'Neither of us has eaten. We are a fine pair.',
      'There is a story behind the scar. It is short and it is not good.',
      'You keep looking out there. It looks back, and it is very boring company.',
      "I talk when it's quiet. You'll learn to sleep through it.",
      'Sit up straight. Not for the posture. It is just nicer to look at.',
    ],
    wave: ['Here we go. Do not be polite about it.', 'Company for you. Try to look busy.'],
    pressure: [
      "Right, that's plenty of them, thank you.",
      'You are doing fine. Keep doing that, but faster.',
    ],
    hurt: [
      'That looked expensive. You will want to not do that again.',
      "You let that one in. I'll allow you the one.",
      'Ow. Say nothing.',
    ],
    close: [
      'One of us has stopped talking and it is not me. That is how this works.',
      'Everyone gets to this bit. Most of them are worse company about it than you.',
    ],
    boon: [
      "There. You've grown. Don't get a personality about it.",
      'The ship likes you better than she likes me.',
      'You need that and you know it. Look at you not saying so.',
    ],
    boss: [
      'Well. That is the largest thing either of us has seen this week.',
      'There is a shape out there doing something enormous. Just so you and I are clear.',
    ],
    bossdown: [
      'Good. Now sit down before you fall down.',
      "That was you. I'll only say it the once.",
    ],
    quiet: [
      'You have stopped doing the thing with your jaw. Good sign, that.',
      'The silence is worse than the noise. Nobody believes that until they have had both.',
      "You're humming. That's mine, that is.",
    ],
    deep: [
      "You're still here. So is the tape on that panel. Neither of you was meant to last.",
      'Long way in for someone who complains this much.',
    ],
    callback: [
      'You remember the {boss}? Course you do. You have stopped talking again.',
      '{minutes} minutes and nobody has died of it. My last crew managed nine.',
      'You went at {firstBoon} without a pause. That told me who you are.',
    ],
    death: [
      'Somebody fix the draught in the second corridor. It gets everyone eventually.',
      'You were always going to be fine. I only talked over the top of it.',
    ],
  },

  'drywit:they': {
    boarding: ["Boots are on. That's usually the whole plan."],
    early: [
      "Somebody welded a mug to the console. It stays. It's load-bearing now.",
      'There is a photograph taped under the seat. Whoever taped it did a poor job. It has held.',
      'This coat came aboard smelling of the last ship. The last ship is not missed. The coat is.',
      'Something ticks behind the ninth panel. The last owner left a note about it. The note says: leave it.',
      'The kettle has survived three crossings and one owner. It has outlasted better.',
      'A song got stuck at the dock and nobody has come to collect it. Not a good song.',
      'The chair has a dent in it from somebody bigger. Wrong shape. Being sat in anyway.',
      "There is a bulb out over the aft hatch. Out for a year. Somebody else's job.",
      'The draught through the seam has a name. Whoever named it is not aboard. The name stuck.',
      'The locker has a spare thermos in it. It belonged to somebody who left. It gets filled anyway.',
    ],
    wave: [
      'Company. The kettle is not going on for this.',
      'Something is arriving. The note behind the ninth panel did not mention this.',
    ],
    pressure: [
      'That is a great many of them. The chair was not built for this.',
      'Crowded. The mug is holding up better than most.',
    ],
    hurt: [
      'Something is leaking that was not leaking before. It can introduce itself later.',
      'That went through the coat. The coat had one good year left.',
      'Straight through the good side of the jacket. Typical.',
    ],
    close: [
      'Everything has gone very far away and is being polite about it.',
      "The console has stopped arguing back. That's new.",
    ],
    boon: [
      'The ship is heavier and nobody consulted the chair.',
      'Something changed. The mug slid two inches. It has never slid before.',
      'New weight. It suits the coat, apparently.',
    ],
    boss: ['Oh. The room got smaller.', 'The bulb over the aft hatch has given up. Fair enough.'],
    bossdown: [
      'It stopped. So did the ticking behind the ninth panel. Nice.',
      'The mug did not move. Somebody give the mug a medal.',
    ],
    quiet: [
      'Nothing out there. The kettle is the loudest thing aboard and it is off.',
      "This is the bit where the tea would've been.",
      'Somebody left a crossword in the locker. Four across has been four across for a week.',
    ],
    deep: [
      'The coat has stopped smelling like the last ship. Long way in.',
      'The second thermos ran out a while back. That is a sort of clock.',
    ],
    callback: [
      'The {hitter} left a dent in the coat. The coat is not being replaced.',
      '{minutes} minutes. Long enough for a second cup, if anybody had made a first.',
      'Still hearing the {boss}. The mug heard it too, apparently. It moved.',
    ],
    death: [
      'The mug is welded to the console. It stays. It was load-bearing.',
      'Somebody fill the second thermos. Empty since the dock, and that was never the arrangement.',
    ],
  },

  // =========================================================================
  // DEVOUT — the run is a rite. The ship is borrowed. Cadence and repetition.
  // =========================================================================
  'liturgist:he': {
    boarding: ['We are lent this hull. We will hand it back. Begin.'],
    early: [
      'I light nothing out here. The dark is the altar. It needs no help.',
      'My father blessed ships. I only borrow them.',
      'Every hour out here is a loan. I have never once made a payment.',
      'We do not conquer the deep. We are permitted through it.',
      'I say the names of the ships that did not return. It takes eleven minutes.',
      'The engine has a hymn in it. Two notes, badly kept.',
      'I do not pray for safety. That is not what prayer is for.',
      'I have washed my hands before every crossing for thirty years. It has never helped. I still do it.',
      'The old crossings had a word for this hour. We forgot the word. We kept the hour.',
      'We are a small noise in a very old room.',
    ],
    wave: [
      'The dark is collecting. I have paid before.',
      'They come as they were sent. My part is to stand.',
    ],
    pressure: [
      'Many. There have always been many, and I have always been one of them.',
      'This is the crowded part of the rite. I have knelt through worse.',
    ],
    hurt: [
      'That is a payment. I said there would be payments.',
      'The debt is called. Some of it.',
      'We are being asked for our share.',
    ],
    close: [
      'The vessel is nearly given back.',
      'I am at the end of what was lent.',
    ],
    boon: [
      'The dark gave us something. It never gives free.',
      'We are heavier. We were meant to be.',
      'Something was offered. I have accepted on our behalf.',
    ],
    boss: ['This one has a name. I will not say it.', 'Kneel or do not. It sees us either way.'],
    bossdown: [
      'It is returned. As we all are.',
      'Something old has stopped. We say nothing for a moment.',
    ],
    quiet: [
      'We are in the held breath between the verses.',
      'Listen. That silence is older than the ship and older than us.',
      'The rite has a pause in it. This is the pause.',
    ],
    deep: [
      'We are past the part with words. Only the walking now.',
      'Long into it. The dark no longer troubles to seem empty.',
    ],
    callback: [
      'The {boss} was our first verse. There are always three.',
      '{minutes} minutes borrowed. Not one of them ours.',
      '{firstBoon} came to us first. Nothing arrives by accident out here.',
    ],
    death: [
      'Everything here was lent to us. Even the breath. Take it back gently.',
      'Say the short prayer. The long one was never for the dying.',
    ],
  },

  'liturgist:she': {
    boarding: ["Old thing. We're crossing you again. Be patient with us."],
    early: [
      'You have had better ships than mine. Take your time with this one.',
      "My grandmother called you the long room. She wasn't wrong.",
      'Talk to it. It is rude to cross something in silence.',
      "You were here before the lights. You'll be here after. I don't hold it against you.",
      "I won't ask you for mercy. You'd only be embarrassed.",
      'Ship. You are borrowed, love. Behave accordingly.',
      "There's a bell at home that only rings for the dead. I hear it out here. I've stopped mentioning it.",
      "You out there. You have been very quiet. That's how it starts.",
      'Bless the hull, bless the seams, bless whoever welded the ninth panel badly.',
      'I say the same words every crossing. Not for you. For me.',
    ],
    wave: ['There you are. Come on, then. Be about it.', 'You are sending them early. Noted.'],
    pressure: [
      "All right. You've made your point. Beautifully.",
      'You brought the whole choir.',
    ],
    hurt: [
      'That will not be enough for you.',
      'Take it. It was always yours.',
      "That's your due. That is not all of me.",
    ],
    close: [
      'You are close now. You have never once been this close and stayed silent.',
      "Don't be gentle. You never were.",
    ],
    boon: [
      'You gave us that. What are you owed?',
      "Thank you. It isn't a habit, don't expect it.",
      'You have dressed the ship for something. I know what.',
    ],
    boss: ['Oh. You came yourself.', 'Look at you. Look what you kept back.'],
    bossdown: [
      "Rest. You aren't the last of them.",
      "There. That's one of your names finished.",
    ],
    quiet: [
      'You are holding your breath. So am I.',
      'Say something. You always say something before.',
      'Hush, then. I can wait as long as you.',
    ],
    deep: [
      'Politeness is over between us. Good. It was overdue.',
      'We are deep in you now. You know it, I know it.',
    ],
    callback: [
      "You sent the {boss} and I'm still here. Send better.",
      '{minutes} minutes you have let us live. I know what that costs you.',
      "Your {hitter} put a mark on me. I'll carry it. Don't fuss.",
    ],
    death: [
      'You were patient with me. I will not argue at the end. Take the ship first, she frightens easier.',
      'Go on, then. You have been very patient and I have been very late.',
    ],
  },

  'liturgist:they': {
    boarding: ['The hull is lent. The hour is lent. One begins anyway.'],
    early: [
      'One bows at this hour. One has always bowed at this hour. Nobody is watching.',
      'A word gets said at the door before every crossing. It was said. Nobody heard it, and that is not the point.',
      'This hour was named once. The name did not survive. The hour did.',
      "There's a name written inside this collar. It is not this one's name. It has never been unpicked.",
      'The words still get said. Belief went out a decade back. The words still work.',
      'Nothing out here is owned. Not the hull, not the air, not the hour.',
      'The engine keeps two notes. The rite asks for three. One has been missing for years.',
      'The kneeling happens at the same point in every crossing. Old arrangement. Nobody renegotiated it.',
      'Everything borrowed comes back. Even this. Especially this.',
      'There is a ledger somewhere with this crossing already written into it. Nobody aboard is the author.',
    ],
    wave: [
      'Ah. The dark is calling in a little of it.',
      'Something is due. It was always going to be due at this hour.',
    ],
    pressure: [
      'The whole congregation, then.',
      'Many. The rite counts none of them. It never has.',
    ],
    hurt: [
      "That's the interest. The principal is still coming.",
      'Something was taken. Nothing here was ever owned.',
      'A payment. Marked in the ledger. Nobody aboard keeps the ledger.',
    ],
    close: [
      'Most of it has been given back now. The rest will not take long.',
      'The rite ends the same way every time. This is where it ends.',
    ],
    boon: [
      'The dark handed something over. Nothing out here is handed over free.',
      'Heavier now. That is how the lending works.',
      'A gift. The ledger has already written down what it costs.',
    ],
    boss: [
      'The room has gone reverent. Bad sign.',
      'Something old is looking. It has looked before, at people who are not aboard.',
    ],
    bossdown: [
      'Returned. Everything is, eventually.',
      'Quiet again. One stands still through this part. That is also the rite.',
    ],
    quiet: [
      'The pause in the middle of the words. This one.',
      'Held breath. That is the whole prayer. It has always been the whole prayer.',
      'Nothing. The rite does not trust nothing either.',
    ],
    deep: [
      'Long crossing. One stopped asking how long a while back.',
      'Deep enough that the borrowed things want returning.',
    ],
    callback: [
      'The {boss} was the first thing asked for. Nothing out here asks once.',
      '{minutes} minutes of borrowed air. None of it owed to anybody aboard.',
      '{firstBoon} arrived first. Out here nothing arrives on its own.',
    ],
    death: [
      'There is a name written inside this collar. Somebody should tell them it got this far.',
      'The hour is over and the ledger is closed. Whoever is next: bow anyway. Nobody is watching.',
    ],
  },

  // =========================================================================
  // NERVY — counts, checks, apologises for asking. Self-interruption. Numbers as comfort.
  // =========================================================================
  'worrier:he': {
    boarding: ["Right. Right. Seals checked. Checked twice. I'm fine."],
    early: [
      "I counted the exits. There's one. I counted it four times anyway.",
      "Is that normal? That sound? I'm going to assume it's normal.",
      'I have eleven checks. I do them in order. If I lose the order I start again.',
      'My hands do this thing when I — anyway. It is fine.',
      'I wrote to my mother before we launched. Twice. The second one was worse.',
      'Sorry. I talk when I am — sorry.',
      'It has been quiet a while. That is either good or it is the other thing.',
      'I brought spare gloves. Three pairs. That is not excessive. Is that excessive?',
      "The air reads fine. It read fine an hour ago too. I'll check again.",
      "I don't mind the dark, I mind not knowing how much of it there is. There isn't a number.",
    ],
    wave: ['How many — no, don\'t tell me. Actually tell me.', 'Here they come, here they come. Okay.'],
    pressure: [
      "That's more than — that is a lot, that is more than the last lot.",
      "I can't count them, I have stopped counting, I'm sorry.",
    ],
    hurt: [
      'Was that bad? Tell me if that was bad.',
      'Okay. Okay. Still eleven seals. Still eleven.',
      'That — sorry, that one went right through me.',
    ],
    close: ["I'm not ready, I need a — I'm not ready.", 'How long? Just — how long?'],
    boon: [
      'Is it meant to feel like that? It feels like something.',
      "Right, something changed, I'll add it to the list. I have a list.",
      'Thank you. Sorry. Thank you.',
    ],
    boss: ['No. No no no. That is not — no.', "How big is it? Don't answer that."],
    bossdown: ['It is down? It is down. Say it again.', 'Okay. Okay. Hands. Stop it. Stop.'],
    quiet: [
      "It's quiet. That's usually the — never mind.",
      'Forty seconds of nothing. Forty-one.',
      "Is it over? It isn't over, is it.",
    ],
    deep: [
      'We have been out here too long. I know exactly how long. That is the problem.',
      'I stopped writing it down about an hour ago. That frightens me more than the rest.',
    ],
    callback: [
      "If the {boss} comes back I don't — I'm not doing that twice.",
      '{minutes} minutes. Counting is the only thing that helps.',
      'The {hitter}. I keep — I keep checking the seam where it hit us.',
    ],
    death: [
      "The numbers are in my coat pocket. Somebody should check them, I might have —",
      'I checked everything. I did check everything. Tell them I checked.',
    ],
  },

  'worrier:she': {
    boarding: ["You checked the seals? Don't answer. I'll check them."],
    early: [
      'You are calm. How are you calm? Teach me that.',
      'Tell me if you hear it too. The tapping. Tell me it is not only me.',
      "Don't say it's fine. Say a number. Numbers I can hold.",
      "You'd tell me if something was wrong, wouldn't you? Say yes even if — just say yes.",
      'My mother says I worry like it is a trade. It is, and you are getting it free.',
      'Give me your face for a second. Good. Okay. You helped, you actually helped.',
      "Talk to me while I do the checks. Doesn't matter what about.",
      'Are your hands cold? Mine are always cold. That is normal, that is only me.',
      'If I go quiet, say my name. Just — say it, and I will come back.',
      "Sorry. Sorry, you didn't ask for the running commentary.",
    ],
    wave: [
      'How many do you see? I see too many.',
      "Okay, okay — you take the left, I'll panic about the right.",
    ],
    pressure: [
      "Don't look at all of them. Look at one. One at a time, please.",
      'Stay where I can see you, stay where I can —',
    ],
    hurt: [
      'Are you hurt? Tell me properly.',
      "That's twice now. I am counting. Someone has to.",
      'Okay. Okay. You are okay. Say it back.',
    ],
    close: [
      'Are you still there? Please — are you still there?',
      "Tell me a number. Any number. You pick it, I'll hold it.",
    ],
    boon: [
      "Does that feel right to you? It feels right, doesn't it.",
      'Take it, take it, I have checked it twice for you.',
      "Better. That's better. Say it's better.",
    ],
    boss: ['Tell me you see it. Tell me right now.', "Don't — don't look straight at it."],
    bossdown: [
      'It is down? Look at me and say it is down.',
      'Okay. Breathe with me. Four in. Come on.',
    ],
    quiet: [
      "Is this the good quiet or the other one? You'd know.",
      'Would you say something? It does not matter what. It really does not.',
      'Thirty seconds. Stay with me for thirty.',
    ],
    deep: [
      "You've been brilliant and I've been counting and I don't know which one is holding.",
      "We are far in. Don't tell me how far. Actually do.",
    ],
    callback: [
      'Watch the left when the {hitter} comes. It always comes from the left. Always.',
      'You are still here after {minutes} minutes. Do you know what that does for me?',
      'If that {boss} turns up again you stay behind me. That is not a request.',
    ],
    death: [
      "Tell my sister it was quick. Two seconds. Say two seconds, she'll believe two.",
      'Stay where I can — no. Go. Go, please, go now.',
    ],
  },

  'worrier:they': {
    boarding: ['Seals checked. Checked again. That makes three. Three is not enough.'],
    early: [
      'The count says eleven. The count has said eleven four times.',
      'Counting keeps it small. Eleven panels. Eleven. Small.',
      "There's a list in this pocket. Written twice, in case.",
      'The checks got done. Then they got done again. The second time does not count and it happens anyway.',
      'Sorry. The counting goes out loud when it goes fast.',
      'Nine went to eleven somewhere. Nobody added anything. Nine went to eleven.',
      'Everything reads normal. Normal was the reading last time as well.',
      'The exits were counted. There is one. It gets counted again in a minute.',
      'There is a number for how far out this is. Nobody says it. Not asking. Asking.',
      'Sorry — the talking fills the gaps between the numbers. The gaps are the bad part.',
    ],
    wave: [
      'There. The count was right. The count is always right and it is never welcome.',
      'There is one more than there was. It got counted before it got seen.',
    ],
    pressure: [
      'Too many for the counting. That is the bad part, the counting stopping.',
      'The number went past where it has a name. It is fine. It is fine.',
    ],
    hurt: [
      "That's two. Two is the number now.",
      'Something is torn. Not counting that one. Counting it.',
      'One. That is one. Nobody ever said what the limit was.',
    ],
    close: [
      'The counting has stopped. It does not stop.',
      'Everything is a long way off and none of it has a number on it.',
    ],
    boon: [
      'Heavier. That gets checked. Then it gets checked again.',
      'Something changed. It goes on the list. The list has a page for changes.',
      'Good. Probably good. Adding it to the count.',
    ],
    boss: [
      'That is not a number. Nobody ever counted for that.',
      'The count went to one and stayed at one. One is worse.',
    ],
    bossdown: [
      'Down. Say it twice. Once was never going to be enough.',
      'That is one. Nobody has said what the total is.',
    ],
    quiet: [
      'Counting the quiet now. Eighteen. Nineteen.',
      'Nothing out there and the count keeps going anyway.',
      'This is the kind that comes before. There is a number for how long it lasts. Nobody wrote it down.',
    ],
    deep: [
      'Long way in. The counting went wrong somewhere and nobody noticed.',
      'The numbers stopped being a comfort about an hour back. They are still being counted.',
    ],
    callback: [
      'The {boss}. That was one. Nobody says whether there is a second.',
      '{minutes} minutes. The counting went wrong about halfway. It is fine.',
      'The {hitter} makes it two. Two was the number an hour ago as well. That was a lie.',
    ],
    death: [
      'Fifty-nine. Fifty-eight. It did help, it always —',
      'Sorry. Nobody has to count anything after this. That is the good part.',
    ],
  },

  // =========================================================================
  // SALT — precedent, and the names of people who are not aboard.
  // =========================================================================
  'veteran:he': {
    boarding: ["Fourth ship. Third crew. Let's not make a habit of the pattern."],
    early: [
      'Petrossian used to check the seams at this hour. He was right to.',
      "I've had this coat longer than I've had most people.",
      "Kasper couldn't sleep out here either. Kasper solved it badly.",
      'Learned the hard way that the quiet stretch is when you eat. So I eat.',
      'There were nine of us on the Marlow run. I write to two.',
      'The old hands never looked out the port. I know why now.',
      "I don't take the good seat any more. Superstition. A bad one.",
      'Nell said the dark smells different past the shelf. She was right about that too.',
      'I keep a name stitched inside the cuff. Not telling you whose.',
      'Twenty years and I still put the left boot on first. Ask Ballard why. You cannot.',
    ],
    wave: ['Same as the Kestrel run. Steady.', 'Here they come. They came like this on my last ship.'],
    pressure: [
      "This is how the Marlow went. Don't do what Marlow did.",
      'Too many. I saw this exact number once. Once was enough.',
    ],
    hurt: [
      "Took worse off Ballard's boy.",
      "That's the one that killed Renji. Not letting it be twice.",
      'Hit. Keep moving. Moving is what got me out last time.',
    ],
    close: [
      "I've been here. Didn't care for it then.",
      'This is where Kasper stopped. Not doing that.',
    ],
    boon: [
      "Had one of these on the Kestrel. Didn't help her. Might help us.",
      "Take it. Nell would have taken it.",
      'Heavier. Good. Heavy came home more often.',
    ],
    boss: [
      "I know that shape. I hoped I didn't.",
      'One of these took the Marlow apart in four minutes.',
    ],
    bossdown: [
      'Four minutes it took them. Took us less.',
      "Down. Petrossian would have liked to see that.",
    ],
    quiet: [
      'This is when we lost the Kestrel. Right in the quiet.',
      "Eat now. You won't get another gap.",
      'Nobody talks in the gap. That is how you know they have done this.',
    ],
    deep: [
      'Past where I turned back last time. Different crew, mind.',
      'Nobody I served with has been this far. Nobody I served with is here.',
    ],
    callback: [
      'The {boss}. Ballard described one of those to me once. He was underselling it.',
      '{minutes} minutes. The Marlow ran nineteen. I have been keeping score against it.',
      "The {hitter} hits the same way the Kestrel's did. Left side. Always left.",
    ],
    death: [
      'Petrossian went like this. I told him it was quick. I owe him an apology.',
      "Twenty years telling people what happens next. Turns out I didn't know either.",
    ],
  },

  'veteran:she': {
    boarding: ["You'll do. Halvard was worse and Halvard came home."],
    early: [
      "You stand like Marit did. She stood like that right up until she didn't.",
      'Ask me about the Kestrel later. Not now. Later, if there is a later.',
      'You are gripping. Ease off. Ballard gripped.',
      "I've buried four. You'd think it teaches you something. It teaches you where to sit.",
      "Halvard used to sing this stretch. Badly. I'd take it back now.",
      'Stay off the port. Do not ask me to explain why.',
      'You remind me of somebody. Do not ask which one.',
      'Eat while it is quiet. That is Marit\'s rule, and Marit made it to sixty.',
      "You know what the old crews called this hour? Neither do I. They wouldn't say it.",
      'Keep talking. The ones who go quiet out here are the ones I end up writing about.',
    ],
    wave: [
      'The Kestrel drew this exact crowd. You do what she did not.',
      'Here they are. Steady, like Marit taught.',
    ],
    pressure: [
      "Don't count them. Ballard counted them.",
      "You'll want to run. Everyone wants to run. Don't.",
    ],
    hurt: [
      "That's nothing. Halvard took worse and complained less.",
      "Halvard's rule. Say where, then say how bad. He got it from somebody also gone.",
      "That one killed a friend of mine. Don't let it near you again.",
    ],
    close: ['Marit looked away at exactly this point. You do the other thing.', 'Not you as well. Stay.'],
    boon: [
      "Take it. Marit wouldn't have, and Marit was wrong once.",
      'Suits you. It suited Halvard too.',
      'Good. That is the kind that brings people home.',
    ],
    boss: [
      "You see it? Say you see it. Ballard didn't.",
      'Stay behind me. Halvard did this from in front.',
    ],
    bossdown: [
      "You did what Ballard couldn't. Don't get proud about it.",
      "Down. Marit would have bought you a drink.",
    ],
    quiet: [
      "Sit down. You'll thank me in six minutes.",
      'This is the gap. Use it. Halvard never did.',
      'Keep the noise up. Silence is how I lost the last one.',
    ],
    deep: [
      "Nobody has taken me this far. Not Marit, not Halvard, not one of them.",
      "You've outlasted my whole last crew. I'd rather you didn't know that.",
    ],
    callback: [
      "You handled the {boss} better than Ballard handled his. He'd hate that.",
      '{minutes} minutes. Marit\'s record was fourteen and she never let me forget it.',
      'You went for {firstBoon} first. So did Halvard. I liked him too.',
    ],
    death: [
      'Halvard, you were right about the deep. You can stop being right now.',
      "Don't write to my sister. Go and see her. Marit sent letters and it wasn't enough.",
    ],
  },

  'veteran:they': {
    boarding: ['Fourth crossing. Renji was aboard for three of them. Begin the fourth.'],
    early: [
      "Ballard's rule covers this hour. Ballard is not available for questions.",
      "This coat came off a bigger man. He didn't need it after.",
      'Renji used to hum through this stretch. The tune still turns up. Renji does not.',
      'Kasper called this hour something. Nobody can be asked what he meant by it.',
      'The port stays unlooked at. Renji covered his. Renji was the sensible one.',
      'The knee went during the Marlow crossing. The Marlow kept the date. Nobody else did.',
      "Left boot first. That was Ballard's. Ballard is not here to see it kept.",
      'Everyone who taught this work anything is gone. The lessons stayed on.',
      'Nine went out on the Marlow. Two of them still get written to. The rest have no address.',
      'Renji swore the cold changes past the shelf. He was right, and it did not save him.',
    ],
    wave: [
      'Ah. The Kestrel drew them like this. Ballard named the shape once.',
      'It arrives in this order every time. Renji named the order wrong and never lived it down.',
    ],
    pressure: [
      'This is the number that took the Marlow. Nobody off the Marlow can confirm it.',
      "Too many. Ballard's advice for too many was: do not count them.",
    ],
    hurt: [
      'Renji died of that exact thing. Marked.',
      'Something is open. Ballard walked home with worse.',
      'Hit. Kasper stopped for less than this.',
    ],
    close: [
      'Kasper turned back at this exact point. Kasper does not get a vote here.',
      'This is the part Renji never described. He described everything else.',
    ],
    boon: [
      "Heavier. Ballard's line was that the heavy ones come home. Ballard came home.",
      'The Kestrel carried something like it. That did not save her. It might do better here.',
      "Something new. The Marlow's crew would have known what to do with it.",
    ],
    boss: [
      'That shape unmade a ship in under five minutes. Ballard timed it and did not outlive the timing.',
      'Renji saw one of these once. He would not say what it looked like. Now nobody can ask.',
    ],
    bossdown: [
      'It took the last crew four minutes. This took less. Renji would have whistled.',
      'Down. Ballard is owed that one.',
    ],
    quiet: [
      "The Kestrel died in a gap like this one. Eat anyway. That was Ballard's rule.",
      'Nothing at all. Renji never liked the nothing, and Renji had been right before.',
      'Nobody speaks during the gap. The ones who taught that are not speaking anywhere now.',
    ],
    deep: [
      'Past where the last crew turned. They turned. Nobody aboard now is turning.',
      'Nobody who taught this work came out this far. Nobody is left to be told.',
    ],
    callback: [
      'The {boss}. Ballard once tried to describe one. He undersold it badly.',
      "{minutes} minutes. Nineteen was the Marlow's best. Ballard kept score, and the score outlived him.",
      'The {hitter} comes in off the left, the way the Kestrel used to. Renji learned that expensively.',
    ],
    death: [
      'This is where Renji stopped talking too. Nobody said it would be quiet.',
      'Renji never said what happens after the quiet. He must have known. He never —',
    ],
  },

  // =========================================================================
  // GREEN — first command. Borrowed vocabulary. Delighted in the wrong places.
  // =========================================================================
  'greenhorn:he': {
    boarding: ["Captain on deck. That's me. I'm — that's me."],
    early: [
      "Section four says a commander should project calm. I'm projecting. Is it working?",
      "I've wanted this since I was nine. Nine!",
      "My mother pressed the collar herself. She used starch. I can't turn my neck.",
      'Permission to be delighted. Granted. By me.',
      'I practised the log entry out loud. In the shuttle. Twice.',
      'The manual calls this a transit lull. It is my favourite chapter.',
      'I have a spare pen. A commander should have a spare pen. It says so.',
      "Sorry, I keep saying my ship. It is my ship. Sorry.",
      'There is a smell out here nobody warned me about. I like it.',
      'First command. Everyone says it like a warning. It is not one.',
    ],
    wave: ["Contacts! I said that right, didn't I. Contacts.", 'Here we go. Here we actually go.'],
    pressure: [
      'That is — the training scenario had nothing like that many.',
      'Formation is gone. Fine. Section nine covers this. Sort of.',
    ],
    hurt: [
      "Ow! Sorry. That's not the — sorry.",
      'That is my first one. Everyone gets a first one.',
      'Right! Noted! Adjusting!',
    ],
    close: [
      'This is not in the — okay. Okay. Thinking.',
      "I'd like to not do this on my first one, please.",
    ],
    boon: [
      "Oh, that's brilliant. That is actually brilliant.",
      "The manual said I'd know when. I know when!",
      'Look at her go. Look at HER go.',
    ],
    boss: ["That is — is that standard? That's not standard.", 'Oh. That is the chapter with the warning box.'],
    bossdown: [
      'WE DID — sorry. Sorry. We did that.',
      "I'm logging this. I'm logging every word of this.",
    ],
    quiet: [
      'Is it always this quiet? I love it. Is that allowed?',
      "I'm going to remember this bit. The quiet bit.",
      'Section two says use the lull to rest. I am too happy to rest.',
    ],
    deep: [
      'Nobody out of my class got this far. Nobody in my class.',
      "I keep waiting to feel like an impostor. It isn't happening. Is that bad?",
    ],
    callback: [
      "I met a {boss}. Me! There's a whole page about the {boss} and I'll have met one.",
      '{minutes} minutes as a captain. A whole life, and then {minutes} minutes.',
      'I chose {firstBoon} first. That is going in the log as a considered decision.',
    ],
    death: [
      'I never got to sign the log properly. I practised the signature and everything.',
      "Tell my mother it was my ship. She'll want the word my in it.",
    ],
  },

  'greenhorn:she': {
    boarding: ["You're my crew. That's the first time I've said that out loud."],
    early: [
      'Am I doing the voice? My instructor says I do a voice.',
      "You've done this before, haven't you? I don't want the number.",
      "Do I call you by rank or by name? The manual says rank. I'd rather name.",
      "Look at that. No, properly look. That's ours to look at.",
      "My mother would want a photograph. Stand still a second — no, I'm joking. Mostly.",
      'If I get something wrong, say it fast and say it plain. Please.',
      "You won't believe how long I have wanted this. Guess. Go on.",
      'Section four says project calm. Am I projecting? Be honest.',
      'I keep touching the collar. It is new. Sorry, it is new.',
      'Thank you for taking me seriously. I noticed you doing it.',
    ],
    wave: [
      'Contacts — did I call that right? Tell me later.',
      'Right! Positions. Your positions. You know yours better than I know mine.',
    ],
    pressure: [
      'Stay close to me. Not for your sake. For mine.',
      "Keep telling me what you're seeing. I can't see all of it.",
    ],
    hurt: [
      'I am supposed to ask about the ship first. I am not going to. You first.',
      'That was my fault. I will say so in the log.',
      'Right. Right — you are fine. We are fine. Good.',
    ],
    close: [
      'Please. Not on my first one. Not with you standing right there.',
      "Tell me what to do. I'll do it, just say it.",
    ],
    boon: [
      "Is that good? That's good, isn't it. Look at your face.",
      "Go on, have it — oh, that's lovely.",
      'We are better. We are actually better. Say we are.',
    ],
    boss: [
      "Tell me that's not what I think it is.",
      "Behind me. I know I'm new. Behind me.",
    ],
    bossdown: [
      'Did you see? Tell me you saw.',
      'You did that. I am putting your name first in the log.',
    ],
    quiet: [
      "Here comes the part where I'm supposed to be serious, isn't it.",
      "Sit with me a minute. Captain's orders. The first ones I've given.",
      'You have done this a hundred times. I have done it once and I will remember this bit.',
    ],
    deep: [
      'You have kept me alive a long way in. That is not a small thing to have done.',
      "Nobody from my class is out here. I'd like them to know.",
    ],
    callback: [
      'You took that {boss} like it was ordinary. It was not ordinary.',
      "{minutes} minutes. That's how long you've kept your brand new captain breathing.",
      "You reached for {firstBoon} before I'd decided. I'm learning from you, you know.",
    ],
    death: [
      "Whoever writes this up — don't put it under pilot error. I read every page. Every one.",
      'You were a good crew. I only had you a little while, but you were a good crew.',
    ],
  },

  'greenhorn:they': {
    boarding: ['First command. Section one says the commander speaks first. Speaking.'],
    early: [
      'Section nine is supposed to cover this. Section nine is one paragraph long.',
      'The manual is in the left pocket. It has been read. It is being carried anyway.',
      'There is a certificate in the case with an actual name printed on it. Printed.',
      'Project calm, says section four. Section four does not say how.',
      'The first log entry has been rehearsed. Out loud. Twice, in the shuttle, alone.',
      'Everybody said the first crossing feels long. No section covers this. It feels short.',
      'The paperwork has a term for this lull. Best chapter in the book.',
      'A spare pen is carried. The manual insists on the spare pen. It actually insists.',
      'Somebody was nine years old and drawing these. That went on the application. They took it anyway.',
      'The word for this ship keeps coming out possessive. The manual is against it. The manual can wait.',
    ],
    wave: [
      'Contacts. Said properly. The log will show it was said properly.',
      'Something is arriving and the log is going to be unbearable about it.',
    ],
    pressure: [
      'Well past the training run. Considerably past. No section for that.',
      'Formation is gone. Nothing in the book covers formation going.',
    ],
    hurt: [
      'First hit. The book promises everybody gets one. The book is being generous.',
      'Right. That goes in the log. Under a heading, probably.',
      "Ow. That's a real one. Nothing in the training run was a real one.",
    ],
    close: [
      'Not on the first crossing. There is no chapter for the first crossing ending.',
      'The thinking has gone blank. The checklist has not. Follow the checklist.',
    ],
    boon: [
      'Heavier. There is a form for this and it will be filled in gladly.',
      'The manual said the ship would tell you. The ship told.',
      'Something new. Chapter eleven, and chapter eleven was never this good.',
    ],
    boss: [
      'That thing has its own warning box. A whole page of warning box.',
      'Chapter six has a photograph of one of those. It did not do it justice.',
    ],
    bossdown: [
      'It is down. Somebody should write that down. That is what the log is for.',
      'Its own entry, that. Its own entry, with a heading.',
    ],
    quiet: [
      'This quiet is supposed to be dreadful. Nothing in the book says why.',
      'The lull. It has a name in the manual. It has a name!',
      'Section two says rest in the lull. Section two has never been this pleased.',
    ],
    deep: [
      'Further than anyone in that classroom. There is no form for that.',
      'The impostor feeling never turned up. The index has nothing on that.',
    ],
    callback: [
      'A {boss}. The book gives the {boss} a full page. The book is going to need another.',
      '{minutes} minutes in the chair. Twenty-odd years of wanting it, then {minutes} minutes.',
      '{firstBoon} came first. The log will call that judgement. The log will be flattering.',
    ],
    death: [
      'That certificate has a name printed on it. Nobody ever says it out loud. Somebody say it.',
      'The whole drill got done. Every step, in order. Nobody in that classroom would believe it.',
    ],
  },

  // =========================================================================
  // COLD — states only what is operationally true. The care leaks out through what
  // gets stated at all.
  // =========================================================================
  'clinician:he': {
    boarding: ['Seals hold. The air is breathable. Proceed.'],
    early: [
      'I sleep four hours. It is sufficient. It has been measured.',
      'There are three hundred and twelve rivets on the forward frame. I have been out here before.',
      'My hands are warm. That is the only relevant fact about my hands.',
      'There are no photographs in this cabin. There is a page of dates, and it is accurate.',
      'The hull ticks as it cools. It is not a fault. I have logged it eleven crossings running.',
      'I was a surgeon. Now I am a captain. The work is similar and the hours are better.',
      'Nineteen breaths a minute is inefficient. Twelve is the useful rate.',
      'I do not drink out here. There is no rule about it. There is a reason.',
      'The dark has no properties. People assign it some. I do not.',
      'I had you sized at the dock. Nobody asked me to. It is the sort of thing I do.',
    ],
    wave: ['Contacts. Range closing. Continue as I briefed.', 'Movement. Numerous. Nothing new.'],
    pressure: [
      'There are more of them than there were. Adjust, or be adjusted.',
      'Too many to name. I am not attempting it either.',
    ],
    hurt: [
      'Impact. Structural, not fatal. Continue.',
      'That was avoidable. Note the angle it came from.',
      'You have been hit. Assess later. Move now.',
    ],
    close: [
      'We have nothing left to spend. Act accordingly.',
      'There is one option left. Take it without discussion.',
    ],
    boon: [
      'The ship will take more than it would have. Use that correctly.',
      'Accepted. Do not become fond of it.',
      'It improves us. Slightly. Slight is sufficient.',
    ],
    boss: [
      'Large. Slow at the turn. That is the only useful thing I have for you.',
      'Do not look at the centre of it. Look at the edges.',
    ],
    bossdown: [
      'Terminated. Resume.',
      'It is finished. You may sit for four seconds.',
    ],
    quiet: [
      'Nothing on approach. Rest. That is an instruction.',
      'Use this. It will not repeat.',
      'Silence is data. I read it as arranging.',
    ],
    deep: [
      'We are past the point where I have numbers. Proceeding on judgement.',
      'This exceeds my prior records. That is not a compliment. It is a warning.',
    ],
    callback: [
      'The {hitter} comes in at a fixed angle. I have corrected for it. So should you.',
      '{minutes} minutes. Nobody I have flown with has held {minutes}.',
      'The {boss} had a rhythm. Four beats. You found it before I said it.',
    ],
    death: [
      'Record it as instantaneous. It was not, but record it as instantaneous.',
      'The correct action now is the one that does not involve me. Take it. Do not narrate it.',
    ],
  },

  'clinician:she': {
    boarding: ['The air is good. The seals hold. You have my attention, which is not given widely.'],
    early: [
      'Your hands are cold. There are gloves in the locker behind you. Second shelf.',
      'You have not eaten. It is measurable in how you turn.',
      'Do not apologise to me. Report to me. They are different.',
      'You are breathing at nineteen. Bring it to twelve. I will wait.',
      'I opened people for a living before this. You may ask once, and only once.',
      'If I stop speaking it is not displeasure. It is concentration. Learn the difference.',
      'The port shows you nothing. Look at the panel instead.',
      'Your size is written down somewhere it will be needed. That was done at the dock.',
      'You want me to say something reassuring. I will say something true instead.',
      'Sit. Not because you are tired. Because standing wastes you.',
    ],
    wave: [
      'Contacts. You take the near ones. I have watched you with the near ones.',
      'Movement. Do exactly what you did last time.',
    ],
    pressure: [
      'Too many to count. Do not try. Take them in threes.',
      'Stay in my line of sight. That is operational, not sentimental.',
    ],
    hurt: [
      'You are hit. Tell me where. One word.',
      'That was the angle I warned you about. Correct it.',
      'The bleeding is not urgent yet. Movement is.',
    ],
    close: [
      'There is very little left. Spend what there is on the door.',
      'Do the thing I taught you. Now, not after you have thought about it.',
    ],
    boon: [
      'Take it. You will use it better than I would.',
      'An improvement. Do not celebrate it. Apply it.',
      'That fits you. I have been watching what fits you.',
    ],
    boss: [
      'Large. It turns slowly. That is your advantage and it is your only one.',
      'Watch the edges of it. The centre is mine to watch.',
    ],
    bossdown: [
      'Finished. Sit down for four seconds. That is an order.',
      'You did the correct thing at the correct time. I will note it.',
    ],
    quiet: [
      'Rest now. Not a kindness. An instruction.',
      'Eat. Now, while I am watching you do it.',
      'You have gone quiet. Good. Stay in it.',
    ],
    deep: [
      'You have gone further than my last three. I am revising what I expected of you.',
      'Past my records now. I will be guessing from here. So will you. Keep pace.',
    ],
    callback: [
      'The {hitter} comes from your left every time. Correct for it the way I do.',
      '{minutes} minutes. My last three did not reach half of that. You should know it.',
      'You chose {firstBoon} first. It told me how you would fly. I was correct.',
    ],
    death: [
      'You have about nine seconds before the compartment goes. Use them on the door, not on me.',
      'Do not come back for me. That is the last correct instruction I have.',
    ],
  },

  'clinician:they': {
    boarding: ['The record opens. Air, seals and the hour, all noted. Proceed.'],
    early: [
      'Pulse reads fifty-eight. The record will show fifty-eight. It has always shown fifty-eight.',
      'There is a list of dates in the breast pocket. No photographs. Photographs are inaccurate.',
      'That ticking is the hull cooling. Eleven crossings of it are on file. Not a fault.',
      'Four hours of sleep. The file says four hours is adequate. The file is not wrong.',
      'The record has no column for how it went. That was a deliberate omission.',
      'This work was learned on people. The paperwork from those years is in a different pocket.',
      'People assign the dark a temperament. No instrument has ever found one.',
      'The breathing reads nineteen. Twelve is the number that works. Both go in.',
      'Somebody got measured at the dock. The figures went on file and the figures were right.',
      'Nothing on this crossing has been unusual. That is a reading, not a reassurance.',
    ],
    wave: [
      'Contacts. Range closing. Nothing here requires a decision yet.',
      'Movement, numerous. It goes in the record as numerous.',
    ],
    pressure: [
      'The record keeps a word for this many. The word is not useful.',
      'More than can be counted. Counting was never the instruction.',
    ],
    hurt: [
      'Impact. Structural. It will be written up later, correctly.',
      'That came in at the angle previously noted. Adjust.',
      'Something is open. It is not arterial. The record can wait.',
    ],
    close: [
      'The readings are all going one direction. That is the entire report.',
      'One option remains. It is being taken. Nobody needs to be consulted.',
    ],
    boon: [
      'An improvement. Marginal. Marginal is the useful kind.',
      'Accepted. Fondness is not required and would not be recorded anyway.',
      'Better than it was. The record will say better. It will not say why.',
    ],
    boss: [
      'Slow through the turn, and large. That is the entire file on it.',
      'Something old, and it is in the record twice. It was not survived twice.',
    ],
    bossdown: [
      'Terminated. It goes in as terminated.',
      'Finished. Four seconds are available. They should be taken.',
    ],
    quiet: [
      'Nothing approaching. Rest is indicated.',
      'Use this. It does not recur.',
      'Silence is information. The record reads it as arranging.',
    ],
    deep: [
      'Past the recorded depth. Proceeding on judgement.',
      'This has gone on longer than is efficient. Noted. Not acted on.',
    ],
    callback: [
      'The {hitter} arrives on one bearing and only one. That is in the record twice now.',
      '{minutes} minutes. No prior record extends that far.',
      'The {boss} had four beats. It was counted before it was named.',
    ],
    death: [
      'The list of dates is in the breast pocket. There is room for one more. Get the year right.',
      'Nothing about this is unusual. Write that down. Write it exactly like that.',
    ],
  },

  // =========================================================================
  // FIRE — talks past you, at the thing in the dark. The dare said out loud.
  // =========================================================================
  'firebrand:he': {
    boarding: ["I'm here. I've been telling you I'd come. Start."],
    early: [
      "I've been shouting at you since I was seventeen. You have never once answered.",
      "My brother said the dark doesn't listen. My brother was quiet all his life.",
      'I never sleep before a crossing. I like arriving loud.',
      'You out there. Big empty. I brought a small ship and a large opinion.',
      "Every crew I've had told me to keep it down. Out here nobody can make me.",
      'I talk to it. Better than the alternative, which is listening.',
      "I've got a scar for every time somebody told me to stand back.",
      'You want me quiet. That is how I know it is working.',
      'The old crews whispered through this stretch. Cowards. Beautiful cowards.',
      "Come on, then. I've nowhere else to be and a great deal to say.",
    ],
    wave: ['There you are. Took your time.', "Send them. Send all of them. I've been bored."],
    pressure: [
      'Yes! More! That is the spirit I was after.',
      'Come on. Crowd me. See what it gets you.',
    ],
    hurt: [
      "That's it? That is your best swing?",
      "Good. Now I'm interested.",
      "You'll have to do that again. And again.",
    ],
    close: [
      'You are not getting a quiet one out of me.',
      'Come closer. I want you close for this.',
    ],
    boon: [
      "Look what I've got now. Look at it.",
      'You gave me that. You will regret giving me that.',
      'Heavier. Better. Come and try me.',
    ],
    boss: ['THERE you are. I knew you were hiding something.', 'Finally. Something worth the noise.'],
    bossdown: ['Get up. Go on. Get up.', "That's one of yours. I'm coming for the rest."],
    quiet: [
      "Don't go shy on me now.",
      'I know you can hear me. You always could.',
      'Silence. Cheap trick. Try another.',
    ],
    deep: [
      'This is your house and I am still standing in it.',
      "I've walked further into you than anyone. Say something.",
    ],
    callback: [
      'Your {boss} is dead and you sent it yourself. What else have you got?',
      "{minutes} minutes I've been in your throat. You haven't swallowed yet.",
      'That {hitter} of yours hit me once. Once.',
    ],
    death: [
      "Come on, then. Closer. I want to see what I've been shouting at.",
      "Don't you dare go quiet now. Not now. Say something.",
    ],
  },

  'firebrand:she': {
    boarding: ["Hello, dark. It's me again. Don't pretend you forgot."],
    early: [
      "You've eaten better captains than me. I intend to be the one that repeats on you.",
      'My mother told me not to talk to it. My mother also never left the dock.',
      "You never answer. One day you will, and I'd like to be there.",
      'I know what you are. Big, patient and bored. So am I.',
      'Go on. Do the cold thing. Do the thing where you get colder.',
      'Every ship you have taken, you took quietly. Not this one.',
      "You listen. Don't tell me you don't listen. You've gone very still.",
      'I sing out here. Not for me. To annoy you.',
      'You have been waiting a long time to meet somebody rude.',
      "I'll say your name if you say mine. No? Thought not.",
    ],
    wave: ['There. Was that so hard?', 'Come on, then. Come and see.'],
    pressure: [
      "All of you? For me? You shouldn't have.",
      'Crowd me. Please. Crowd me.',
    ],
    hurt: [
      'Was that meant to end it? Was it?',
      'Again. Do it again, I dare you.',
      "That's mine now. It stays with me.",
    ],
    close: [
      "You'll have to come and take it. I'm not handing it over.",
      'Look at me while you do it. Look.',
    ],
    boon: [
      "See what you've done? You have made me worse.",
      "Thank you. Genuinely. You'll hate that.",
      "Now I've got something for you.",
    ],
    boss: ["Oh, you sent the big one. I'm flattered.", 'Is that the best of you? Say it is. Lie to me.'],
    bossdown: [
      'That was yours. Was it precious?',
      "One down. Are you counting? I'm counting.",
    ],
    quiet: [
      'Sulking? You are sulking.',
      'Gone sullen, have you. You are better company angry.',
      'Nothing out of you. That is not restraint. That is technique, and it is old.',
    ],
    deep: [
      'No more pretending to be empty. About time.',
      "This far in and you still haven't shown me your face.",
    ],
    callback: [
      "Your {boss} is finished. Send the next one, I'm warm now.",
      "{minutes} minutes in your mouth and you still haven't bitten.",
      "Your {hitter} marked me. I marked you back. You just can't see it.",
    ],
    death: [
      "You brought all of it. All of it, for one woman and a tired ship. I'll take that.",
      'Was that it? Was that all you had? Oh. Oh, you were saving it.',
    ],
  },

  'firebrand:they': {
    boarding: ['The shout in this chest has been waiting for you. Letting it out slowly.'],
    early: [
      'The teeth are already set. The dark did that, not this one.',
      'The shout has been in this chest since the dock. It has not got quieter.',
      'The hands want it. The head is only along to watch.',
      'You out there. The body has noticed you looking.',
      'Somebody was told to keep quiet their whole life. Not out here.',
      'The blood is up and nothing has happened yet. Promising.',
      "This one talks to the dark. The dark has never once been polite back.",
      'Somebody said stand back once too often. There is a mark for each time they said it.',
      'The chest is louder than the engine. Always has been.',
      'Go on, then. Say something. The body is listening even if nobody else is.',
    ],
    wave: [
      'There. The hands were right to be up.',
      'Something is coming and this body is glad about it.',
    ],
    pressure: ['Crowded. The teeth approve.', 'All of them at once. Fine. Fine!'],
    hurt: [
      'That woke something up. Poor decision, that.',
      'The ribs took it. The mouth has notes.',
      'Again. The body dares you.',
    ],
    close: [
      'The shout in here is not finished. It has never once been finished.',
      'The hands are still up. Come and put them down.',
    ],
    boon: [
      'Heavier. The shout in here approves.',
      "The dark handed that over. It'll want it back. Let it try.",
      'The shoulders have plans now.',
    ],
    boss: [
      'The chest got louder before the eyes worked out why.',
      'There it is. The body has been waiting on that.',
    ],
    bossdown: [
      'Down. Nothing in here has lowered its voice.',
      'That was one of yours. The teeth are still out.',
    ],
    quiet: [
      'Sulking, then. The chest has not quietened.',
      'Nothing out there. Something in here disagrees.',
      "The body doesn't do quiet. Never learned.",
    ],
    deep: [
      'Deep as this and the chest is louder, not quieter.',
      'The dark dropped the act a while back. So did this one.',
    ],
    callback: [
      'The {boss} is finished and the hands are still shaking. Not from fear.',
      '{minutes} minutes in and nothing in this chest has sat down.',
      'That {hitter} got one in. The body kept the mark on purpose.',
    ],
    death: [
      'Still shouting. Nothing out there has answered yet. Somebody stay and listen for it.',
      'The teeth are still out. Whatever comes next can find that out for itself.',
    ],
  },
};

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/** Every voice, keyed `<temperament>:<pronoun>`. 21 of them; none share a line. */
export const LINES: Readonly<Record<VoiceKey, VoiceBank>> = VOICES;

/** The bank for a captain. Never throws: every voice key is populated. */
export function bankFor(captain: Captain): VoiceBank {
  return VOICES[voiceKey(captain)];
}

/** How many lines each voice owns, and the total. Used by the volume test. */
export function countLines(): { perVoice: Record<VoiceKey, number>; total: number } {
  const perVoice = {} as Record<VoiceKey, number>;
  let total = 0;
  for (const t of TEMPERAMENT_IDS) {
    for (const p of PRONOUN_IDS) {
      const key = `${t}:${p}` as VoiceKey;
      const bank = VOICES[key];
      let n = 0;
      for (const b of BUCKETS) n += bank[b].length;
      n += bank.callback.length + bank.death.length;
      perVoice[key] = n;
      total += n;
    }
  }
  return { perVoice, total };
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/**
 * Per-run speech state. Holds no timers and no clock; the component owns pacing.
 *
 * `spent` is the once-only ledger — the boarding line and the death line go in it and
 * never come out. There is no persistence anywhere near it, by design: a captain's death
 * line is used up and the captain is gone.
 */
export interface LineState {
  /** Last index picked per bucket. Depth-1 anti-repeat, as the source does. */
  last: Partial<Record<Bucket | 'callback', number>>;
  spent: Set<string>;
}

export function makeLineState(): LineState {
  return { last: {}, spent: new Set<string>() };
}

/**
 * Pick an index that is not the previous one, without the source's unbounded `do/while`.
 * Steps forward on collision instead of resampling — same distribution at these bank
 * sizes, and it cannot spin on a one-entry pool.
 */
function pickIndex(rng: RngStream, length: number, last: number | undefined): number {
  if (length <= 1) return 0;
  const i = rng.next_int(length);
  return i === last ? (i + 1) % length : i;
}

/**
 * A line for this bucket, or `null` when the bucket is spent. Deterministic given the
 * stream: the same captain in the same run says the same things in the same order.
 */
export function pickLine(
  key: VoiceKey,
  bucket: Bucket,
  rng: RngStream,
  state: LineState
): string | null {
  const bank = VOICES[key][bucket];
  if (bank.length === 0) return null;
  if (ONCE_BUCKETS.includes(bucket)) {
    const id = `bucket:${bucket}`;
    if (state.spent.has(id)) return null;
    state.spent.add(id);
    return bank[0];
  }
  const i = pickIndex(rng, bank.length, state.last[bucket]);
  state.last[bucket] = i;
  return bank[i];
}

/**
 * A late-run line that refers to something that actually happened, already filled. Returns
 * `null` until the run has produced at least one memory a callback can stand on.
 */
export function pickCallback(
  key: VoiceKey,
  rng: RngStream,
  state: LineState,
  memory: RunMemory
): string | null {
  const bank = VOICES[key].callback;
  const ready: string[] = [];
  for (const line of bank) if (callbackReady(line, memory)) ready.push(line);
  if (ready.length === 0) return null;
  const i = pickIndex(rng, ready.length, state.last.callback);
  state.last.callback = i;
  return fillSlots(ready[i], memory);
}

/**
 * The death line. Ranked, best first, and taken in order rather than at random so the
 * strongest one is the one that plays. Consumed for the session: ask twice for the same
 * voice and the second captain gets the second line, never the first again.
 *
 * Returns `null` only if a session somehow burns every line for a voice, which the caller
 * should treat as "say nothing" rather than "say something weaker".
 */
export function takeDeathLine(key: VoiceKey, state: LineState): string | null {
  const bank = VOICES[key].death;
  for (let i = 0; i < bank.length; i++) {
    const id = `death:${key}:${i}`;
    if (state.spent.has(id)) continue;
    state.spent.add(id);
    return bank[i];
  }
  return null;
}

/** All of a voice's death lines, ranked. For tests and for the writer's own eyes. */
export function deathLines(key: VoiceKey): readonly string[] {
  return VOICES[key].death;
}

/** `<temperament>:<pronoun>` from the parts, for callers that have no `Captain` to hand. */
export function keyOf(temperament: TemperamentId, pronoun: PronounId): VoiceKey {
  return `${temperament}:${pronoun}`;
}
